import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../api/supabase';
import { ACCOUNTS } from '../../api/accounts';
import { getClientDepositBalance, computeFifoAllocation, applyClientDeposit } from '../../api/receiptAllocations';
import { resolveVoucherRefs } from '../../api/voucherRefs';
import { nicknameOnly } from '../../utils/clientIdentity';
import { useAuth } from '../../context/AuthContext';
import { downloadCsv } from '../../utils/downloadCsv';

const fmt = (n) => Number(n || 0).toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB') : '-';

const TODAY = new Date();
const daysBetween = (dateStr) => {
  if (!dateStr) return 999;
  return Math.floor((TODAY - new Date(dateStr)) / 86400000);
};
const ageBucket = (days) => {
  if (days <= 30) return '0-30';
  if (days <= 60) return '31-60';
  if (days <= 90) return '61-90';
  return '90+';
};

export default function ClientLedger() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const username = user?.email?.split('@')[0] || 'unknown';
  const [searchParams] = useSearchParams();
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientSearch, setClientSearch] = useState('');
  const [txns, setTxns] = useState([]);
  const [showReversed, setShowReversed] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [summaryError, setSummaryError] = useState(null);
  const [error, setError] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [summaryData, setSummaryData] = useState({});
  const [depositData, setDepositData] = useState({});
  const [selectedDepositBalance, setSelectedDepositBalance] = useState(0);
  const [sortKey, setSortKey] = useState('balance');
  const [sortDir, setSortDir] = useState('desc');
  const [groupView, setGroupView] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [branches, setBranches] = useState([]);
  const [branchFilter, setBranchFilter] = useState('');

  const fetchAll = async (table, query) => {
    const PAGE = 1000;
    let all = [], from = 0, done = false;
    while (!done) {
      const { data, error } = await query(supabase.from(table)).range(from, from + PAGE - 1);
      if (error || !data?.length) break;
      all = all.concat(data);
      if (data.length < PAGE) done = true;
      else from += PAGE;
    }
    return all;
  };

  useEffect(() => {
    fetchAll('clients', q => q.select('id,client_code,full_name,phone,agents:care_of_agent_id(tls_code)').order('full_name'))
      .then(data => {
        setClients(data || []);
        const wantId = searchParams.get('client');
        if (wantId) {
          const match = (data || []).find(c => c.id === wantId);
          if (match) setSelectedClient(match);
        }
      });
    supabase.from('branches').select('id, name, code').eq('is_active', true).order('name').then(({ data }) => setBranches(data || []));
  }, []); // eslint-disable-line

  useEffect(() => {
    async function loadSummary() {
      setLoadingSummary(true);
      setSummaryError(null);
      try {
        // Scoped to the AR account only — this is the receivable sub-ledger. Client
        // Deposits (2010) is a separate liability pool, loaded independently below, so
        // "Invoiced"/"Received" here only ever reflect genuine invoices and genuine
        // settlements of the receivable (including a deposit being applied to an
        // invoice, which is a real settlement — just funded from a deposit rather than
        // fresh cash). It never gets diluted by the other leg of a deposit application,
        // which belongs to the deposits pool, not AR.
        const [arLines, depositLines] = await Promise.all([
          fetchAll('journal_lines', q => {
            // Explicit order is required once this crosses 1000 rows (it currently sits at
            // 1,209) — without it, Postgres doesn't guarantee a stable row order between
            // paginated .range() calls, and rows can silently fall through the page boundary.
            let r = q
              .select('base_debit, base_credit, party_id, journal_entries!inner(entry_date, status, is_reversed, reversal_of)')
              .eq('account_id', ACCOUNTS.AR)
              .eq('party_type', 'client')
              .eq('journal_entries.status', 'POSTED')
              .order('id');
            if (branchFilter) r = r.eq('branch_id', branchFilter);
            return r;
          }),
          fetchAll('journal_lines', q => {
            let r = q
              .select('base_debit, base_credit, party_id, journal_entries!inner(status, is_reversed, reversal_of)')
              .eq('account_id', ACCOUNTS.CLIENT_DEPOSITS)
              .eq('party_type', 'client')
              .eq('journal_entries.status', 'POSTED');
            if (branchFilter) r = r.eq('branch_id', branchFilter);
            return r;
          }),
        ]);

        // A reversed entry and its reversal net to zero — exclude both from gross
        // Invoiced / Received, or a corrected mistake inflates these totals.
        const notReversed = l => !l.journal_entries?.is_reversed && !l.journal_entries?.reversal_of;
        const map = {};
        (arLines || []).filter(notReversed).forEach(l => {
          const id = l.party_id;
          if (!id) return;
          if (!map[id]) map[id] = { debit: 0, credit: 0, oldestUnpaid: null };
          const dr = Number(l.base_debit || 0);
          const cr = Number(l.base_credit || 0);
          map[id].debit += dr;
          map[id].credit += cr;
          const date = l.journal_entries?.entry_date;
          if (dr > 0 && date && (!map[id].oldestUnpaid || date < map[id].oldestUnpaid))
            map[id].oldestUnpaid = date;
        });

        const depositMap = {};
        (depositLines || []).filter(notReversed).forEach(l => {
          const id = l.party_id;
          if (!id) return;
          depositMap[id] = (depositMap[id] || 0) + Number(l.base_credit || 0) - Number(l.base_debit || 0);
        });

        setSummaryData(map);
        setDepositData(depositMap);
      } catch (e) {
        setSummaryError(e.message);
      } finally {
        setLoadingSummary(false);
      }
    }
    loadSummary();
  }, [branchFilter]);

  const summaryRows = useMemo(() => {
    const rows = clients
      .map(c => {
        const d = summaryData[c.id] || null;
        if (!d) return null;
        const balance = d.debit - d.credit;
        const bk = balance > 0 ? ageBucket(daysBetween(d.oldestUnpaid || '2000-01-01')) : null;
        return {
          ...c,
          debit: d.debit,
          credit: d.credit,
          balance,
          depositBalance: depositData[c.id] || 0,
          b0_30:   bk === '0-30'   ? balance : 0,
          b31_60:  bk === '31-60'  ? balance : 0,
          b61_90:  bk === '61-90'  ? balance : 0,
          b90plus: bk === '90+'    ? balance : 0,
        };
      })
      .filter(Boolean);

    rows.sort((a, b) => {
      let av = a[sortKey] ?? 0;
      let bv = b[sortKey] ?? 0;
      if (typeof av === 'string') av = av.toLowerCase();
      if (typeof bv === 'string') bv = bv.toLowerCase();
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return rows;
  }, [clients, summaryData, sortKey, sortDir]);

  const grandTotals = useMemo(() => summaryRows.reduce((acc, r) => ({
    debit: acc.debit + r.debit,
    credit: acc.credit + r.credit,
    balance: acc.balance + r.balance,
    depositBalance: acc.depositBalance + r.depositBalance,
    b0_30: acc.b0_30 + r.b0_30,
    b31_60: acc.b31_60 + r.b31_60,
    b61_90: acc.b61_90 + r.b61_90,
    b90plus: acc.b90plus + r.b90plus,
  }), { debit: 0, credit: 0, balance: 0, depositBalance: 0, b0_30: 0, b31_60: 0, b61_90: 0, b90plus: 0 }), [summaryRows]);

  // Must be before early return — hooks cannot be called after conditional returns
  const agentGroupsMemo = useMemo(() => {
    const groups = {};
    summaryRows.forEach(r => {
      const match = r.client_code?.match(/^([A-Z]+-\d+)/);
      const prefix = match ? match[1] : r.client_code;
      if (!groups[prefix]) groups[prefix] = { prefix, clients: [], debit: 0, credit: 0, balance: 0 };
      groups[prefix].clients.push(r);
      groups[prefix].debit += r.debit;
      groups[prefix].credit += r.credit;
      groups[prefix].balance += r.balance;
    });
    return Object.values(groups).sort((a, b) => b.balance - a.balance);
  }, [summaryRows]);

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  const SortIcon = ({ col }) => {
    if (sortKey !== col) return <span style={{ opacity: 0.3, marginLeft: 3 }}>↕</span>;
    return <span style={{ marginLeft: 3, color: 'var(--primary)' }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  const loadLedger = useCallback(async (client) => {
    if (!client) return;
    setLoadingDetail(true);
    setError(null);
    setTxns([]);
    try {
      // Read this client's AR movements straight from the general ledger, plus their
      // Client Deposits activity (advances received but not yet applied to an invoice —
      // per the accounting rules, these must still show on the client's ledger, they just
      // shouldn't move the AR "Balance Due" figure below). A deposit that HAS been applied
      // to an invoice already shows via its AR-side row ("Deposit Applied") — we exclude
      // that same event's Deposits-side leg here so it isn't listed twice.
      const typeLabel = (rt) => {
        switch (rt) {
          case 'TICKET': return 'Ticket';
          case 'VISA': return 'Visa';
          case 'BOOKING': return 'Package';
          case 'RECEIPT_VOUCHER': return 'Receipt';
          case 'DEPOSIT_APPLICATION': return 'Deposit Applied';
          case 'PAYMENT_VOUCHER': return 'Payment';
          case 'CHEQUE_RETURN': return 'Cheque Returned';
          case 'RECEIPT_VOID': return 'Receipt Voided';
          default: return rt || 'Journal';
        }
      };

      const baseSelect = 'id, base_debit, base_credit, description, journal_entries!inner(id, entry_date, entry_number, description, reference_type, reference_id, status, is_reversed, reversal_of)';
      let arQ = supabase.from('journal_lines').select(baseSelect)
        .eq('account_id', ACCOUNTS.AR)
        .eq('party_type', 'client')
        .eq('party_id', client.id)
        .eq('journal_entries.status', 'POSTED');
      let depQ = supabase.from('journal_lines').select(baseSelect)
        .eq('account_id', ACCOUNTS.CLIENT_DEPOSITS)
        .eq('party_type', 'client')
        .eq('party_id', client.id)
        .eq('journal_entries.status', 'POSTED');
      if (dateFrom) { arQ = arQ.gte('journal_entries.entry_date', dateFrom); depQ = depQ.gte('journal_entries.entry_date', dateFrom); }
      if (dateTo)   { arQ = arQ.lte('journal_entries.entry_date', dateTo);   depQ = depQ.lte('journal_entries.entry_date', dateTo); }
      if (branchFilter) { arQ = arQ.eq('branch_id', branchFilter); depQ = depQ.eq('branch_id', branchFilter); }
      const [{ data: arLines, error: arErr }, { data: depLines, error: depErr }] = await Promise.all([arQ, depQ]);
      if (arErr) throw arErr;
      if (depErr) throw depErr;

      const lines = [
        ...(arLines || []).map(l => ({ ...l, is_deposit_row: false })),
        ...(depLines || []).filter(l => l.journal_entries?.reference_type !== 'DEPOSIT_APPLICATION').map(l => ({ ...l, is_deposit_row: true })),
      ];

      getClientDepositBalance(client.id).then(setSelectedDepositBalance);

      // A JE number is internal — show the RV/PV behind each row instead, including the
      // source receipt of a "Deposit Applied" row (see resolveVoucherRefs).
      const voucherRefs = await resolveVoucherRefs(lines.map(l => ({ ...l, party_type: 'client', party_id: client.id })));

      // Invoice-backed lines (INVOICE_TICKET/INVOICE_VISA/INVOICE_BOOKING) should show the
      // invoice number (e.g. TLS-26-0012), not the underlying journal entry number.
      const invIds = [...new Set((lines || [])
        .filter(l => ['INVOICE_TICKET', 'INVOICE_VISA', 'INVOICE_BOOKING', 'invoice'].includes(l.journal_entries?.reference_type) && l.journal_entries?.reference_id)
        .map(l => l.journal_entries.reference_id))];
      let invNumberMap = new Map();
      if (invIds.length) {
        const { data: invs } = await supabase.from('invoices').select('id, invoice_number').in('id', invIds);
        invNumberMap = new Map((invs || []).map(i => [i.id, i.invoice_number]));
      }

      const rows = (lines || []).map(l => {
        const je = l.journal_entries;
        const refType = je?.reference_type;
        const refId = je?.reference_id;
        let drillPath = null;
        let typeText;
        if (l.is_deposit_row) {
          // A Deposits-account row that isn't a DEPOSIT_APPLICATION (those are filtered
          // out above) is either money coming in with no invoice to apply it to yet, or
          // a debit reducing that pool some other way (e.g. a refund).
          typeText = Number(l.base_credit || 0) > 0 ? 'Deposit Received' : 'Deposit Refunded';
          if (refType === 'RECEIPT_VOUCHER' && refId) drillPath = `/vouchers/receipt/${refId}`;
          else if (je?.id) drillPath = `/journals/${je.id}`;
        } else {
          typeText = typeLabel(refType);
          // InvoiceNew.js actually posts INVOICE_TICKET/INVOICE_VISA/INVOICE_BOOKING with
          // reference_id = the invoice itself — 'TICKET'/'VISA'/'BOOKING' never matched
          // anything real, so every invoice line was silently falling through to the raw
          // journal entry.
          if      (refType === 'INVOICE_TICKET' || refType === 'INVOICE_VISA' || refType === 'INVOICE_BOOKING') drillPath = refId ? `/invoices/${refId}` : null;
          else if (refType === 'invoice'         && refId) drillPath = `/invoices/${refId}`;
          else if (refType === 'TICKET'  && refId) drillPath = `/tickets/${refId}`;
          else if (refType === 'VISA'    && refId) drillPath = `/visas/${refId}`;
          else if (refType === 'BOOKING' && refId) drillPath = `/bookings/${refId}`;
          else if (['RECEIPT_VOUCHER', 'CHEQUE_RETURN', 'RECEIPT_VOID'].includes(refType) && refId) drillPath = `/vouchers/receipt/${refId}`;
          else if (refType === 'PAYMENT_VOUCHER' && refId) drillPath = `/vouchers/payment/${refId}`;
          else if (refType === 'DEPOSIT_APPLICATION' && refId) drillPath = `/invoices/${refId}`;
          else if (je?.id) drillPath = `/journals/${je.id}`;
        }
        return {
          txn_id: l.id,
          txn_date: je?.entry_date,
          txn_type: typeText,
          ref_number: voucherRefs.get(je?.id)
            || (['INVOICE_TICKET', 'INVOICE_VISA', 'INVOICE_BOOKING', 'invoice'].includes(refType) && refId && invNumberMap.get(refId))
            || je?.entry_number || '',
          narration: l.description || je?.description || '',
          debit: Number(l.base_debit || 0),
          credit: Number(l.base_credit || 0),
          sort_key: je?.entry_date || '',
          drill_path: drillPath,
          is_reversed_pair: !!(je?.is_reversed || je?.reversal_of),
          is_deposit_row: !!l.is_deposit_row,
        };
      });

      rows.sort((a, b) => (a.sort_key || '').localeCompare(b.sort_key || ''));
      setTxns(rows);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoadingDetail(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => {
    if (selectedClient) loadLedger(selectedClient);
  }, [selectedClient, loadLedger]);

  const [applyingDeposit, setApplyingDeposit] = useState(false);

  // Applies an existing unapplied Client Deposits balance against this client's
  // outstanding invoices, oldest first — same FIFO engine already used when a fresh
  // receipt is allocated. A deposit received before any invoice existed (or one nobody
  // got around to applying) otherwise just sits idle even once there's a real balance
  // due to clear it against.
  const handleApplyDepositFifo = async () => {
    if (!selectedClient || selectedDepositBalance <= 0 || applyingDeposit) return;
    // Show busy while working out the allocation too — that step queries every invoice,
    // and an error here used to be swallowed, so the click looked like it did nothing.
    setApplyingDeposit(true);
    let allocations;
    try {
      allocations = await computeFifoAllocation(selectedClient.id, selectedDepositBalance);
    } catch (e) {
      setApplyingDeposit(false);
      alert('Could not work out the FIFO allocation: ' + e.message);
      return;
    }
    setApplyingDeposit(false);
    // Old balance (opening balance) is paid first, then invoices — both are real AR to clear.
    const applyAllocs = allocations.filter(a => a.target_type === 'invoice' || a.target_type === 'old_balance');
    if (!applyAllocs.length) {
      alert('No outstanding balance or invoices to apply this deposit to.');
      return;
    }
    const summary = applyAllocs.map(a => `${a.label}: LKR ${fmt(a.amount)}`).join('\n');
    const leftover = selectedDepositBalance - applyAllocs.reduce((s, a) => s + a.amount, 0);
    const confirmMsg = `Apply LKR ${fmt(selectedDepositBalance)} deposit via FIFO?\n\n${summary}` +
      (leftover > 0.01 ? `\n\nLKR ${fmt(leftover)} will remain unapplied (nothing more outstanding).` : '');
    if (!window.confirm(confirmMsg)) return;

    setApplyingDeposit(true);
    try {
      for (const a of applyAllocs) {
        const label = a.target_type === 'old_balance' ? 'Old Balance' : a.label;
        await applyClientDeposit(selectedClient.id, a.invoice_id || null, a.amount, username, `Deposit applied (FIFO) — ${label}`);
      }
      await loadLedger(selectedClient);
      getClientDepositBalance(selectedClient.id).then(setSelectedDepositBalance);
    } catch (e) {
      alert('Failed to apply deposit: ' + e.message);
    } finally {
      setApplyingDeposit(false);
    }
  };

  const reversedCount = txns.filter(t => t.is_reversed_pair).length;
  const visibleTxns = useMemo(() => {
    const filtered = showReversed ? txns : txns.filter(t => !t.is_reversed_pair);
    let running = 0;
    // Deposit rows are shown for visibility but don't touch the AR running balance —
    // they're a separate liability pool until actually applied to an invoice.
    return filtered.map(r => { if (!r.is_deposit_row) running += r.debit - r.credit; return { ...r, running_balance: running }; });
  }, [txns, showReversed]);
  const arTxns = useMemo(() => visibleTxns.filter(r => !r.is_deposit_row), [visibleTxns]);
  const totalDebit = arTxns.reduce((s, r) => s + r.debit, 0);
  const totalCredit = arTxns.reduce((s, r) => s + r.credit, 0);
  const balance = totalDebit - totalCredit;

  const handleExport = () => {
    if (!visibleTxns.length) return;
    downloadCsv([['Date','Type','Reference','Narration','Debit (LKR)','Credit (LKR)','AR Balance (LKR)'],
      ...visibleTxns.map(t => [fmtDate(t.txn_date), t.txn_type, t.ref_number, t.narration, t.debit.toFixed(2), t.credit.toFixed(2), t.is_deposit_row ? 'not AR' : t.running_balance.toFixed(2)])
    ], `ledger-${selectedClient?.client_code}.csv`);
  };

  const exportSummary = () => {
    downloadCsv([['Client Code','Client Name','Total Invoiced','Total Received','Balance Due','Deposit Balance','0-30 Days','31-60 Days','61-90 Days','90+ Days'],
      ...summaryRows.map(r => [r.client_code, r.full_name, r.debit.toFixed(2), r.credit.toFixed(2), r.balance.toFixed(2), r.depositBalance.toFixed(2), r.b0_30.toFixed(2), r.b31_60.toFixed(2), r.b61_90.toFixed(2), r.b90plus.toFixed(2)])
    ], 'client-receivables-summary.csv');
  };

  // ── DETAIL VIEW ──
  if (selectedClient) return (
    <div style={{ display: 'flex', height: 'calc(100vh - 80px)' }}>
      <div style={{ width: 240, borderRight: '1px solid var(--border)', display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
        <div style={{ padding: '12px 10px 8px', borderBottom: '1px solid var(--border)' }}>
          <button className="btn btn-secondary btn-sm" style={{ width: '100%', marginBottom: 8 }} onClick={() => setSelectedClient(null)}>← All Clients</button>
          <input className="input" placeholder="Search..." value={clientSearch} onChange={e => setClientSearch(e.target.value)} style={{ fontSize: 12 }} />
        </div>
        <div style={{ overflowY: 'auto', flex: 1 }}>
          {clients.filter(c => c.full_name?.toLowerCase().includes(clientSearch.toLowerCase()) || c.client_code?.toLowerCase().includes(clientSearch.toLowerCase())).map(c => (
            <div key={c.id} onClick={() => setSelectedClient(c)} style={{ padding: '9px 10px', cursor: 'pointer', borderBottom: '1px solid var(--border)', background: selectedClient?.id === c.id ? '#eff6ff' : 'transparent', borderLeft: selectedClient?.id === c.id ? '3px solid var(--primary)' : '3px solid transparent' }}>
              {c.agents?.tls_code && <div style={{ fontSize: 10, color: '#f07040', fontWeight: 600 }}>{c.agents.tls_code}</div>}
              <div style={{ fontSize: 12, fontWeight: 500 }}>{c.full_name}</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{nicknameOnly(c.client_code) || c.client_code}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: 24 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            {selectedClient.agents?.tls_code && <div style={{ fontSize: 12, color: '#f07040', fontWeight: 600, marginBottom: 2 }}>{selectedClient.agents.tls_code}</div>}
            <h2 style={{ margin: 0, fontSize: 20 }}>{selectedClient.full_name}</h2>
            <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{nicknameOnly(selectedClient.client_code) || selectedClient.client_code}{selectedClient.phone ? ` · ${selectedClient.phone}` : ''}</div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <input type="date" className="input" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={{ fontSize: 12, width: 140 }} />
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>to</span>
            <input type="date" className="input" value={dateTo} onChange={e => setDateTo(e.target.value)} style={{ fontSize: 12, width: 140 }} />
            <button className="btn btn-secondary btn-sm" onClick={() => loadLedger(selectedClient)}>Apply</button>
            <button className="btn btn-secondary btn-sm" onClick={handleExport}>CSV</button>
            <button className="btn btn-primary btn-sm" onClick={() => navigate('/vouchers/receipt/new')}>+ Receipt</button>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 20 }}>
          {[
            { label: 'Total Invoiced', value: fmt(totalDebit), color: '#1d4ed8' },
            { label: 'Total Received', value: fmt(totalCredit), color: '#16a34a' },
            { label: balance >= 0 ? 'Balance Due' : 'Credit Balance', value: fmt(Math.abs(balance)), color: balance >= 0 ? '#dc2626' : '#16a34a' },
          ].map(c => (
            <div key={c.label} style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 8, padding: '14px 16px' }}>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>{c.label}</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: c.color }}>LKR {c.value}</div>
            </div>
          ))}
          <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 8, padding: '14px 16px' }}>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>Deposit Balance (unapplied)</div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: selectedDepositBalance > 0 ? '#7c3aed' : 'var(--text-muted)' }}>LKR {fmt(selectedDepositBalance)}</div>
              {selectedDepositBalance > 0 && balance > 0 && (
                <button className="btn btn-secondary btn-sm" disabled={applyingDeposit} onClick={handleApplyDepositFifo} title="Apply this deposit against outstanding invoices, oldest first">
                  {applyingDeposit ? 'Applying…' : 'Apply (FIFO)'}
                </button>
              )}
            </div>
          </div>
        </div>

        {error && <div style={{ color: '#dc2626', marginBottom: 12, fontSize: 13 }}>Error: {error}</div>}

        {loadingDetail ? (
          <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}>Loading transactions...</div>
        ) : txns.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}>No transactions found for this client</div>
        ) : (
          <>
            {reversedCount > 0 && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-muted)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={showReversed} onChange={e => setShowReversed(e.target.checked)} />
                  Show {reversedCount} reversed/corrected entr{reversedCount === 1 ? 'y' : 'ies'} (excluded from totals above)
                </label>
              </div>
            )}
          <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '2px solid var(--border)' }}>
                  {['Date','Type','Reference','Narration','Debit (LKR)','Credit (LKR)','AR Balance'].map(h => (
                    <th key={h} style={{ padding: '10px 12px', textAlign: ['Debit (LKR)','Credit (LKR)','AR Balance'].includes(h) ? 'right' : 'left', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleTxns.map((t, i) => (
                  <tr key={t.txn_id + i}
                    onClick={() => t.drill_path && navigate(t.drill_path)}
                    style={{ borderBottom: '1px solid var(--border)', background: i % 2 ? '#fafafa' : 'white', cursor: t.drill_path ? 'pointer' : 'default', opacity: t.is_reversed_pair ? 0.55 : 1, fontStyle: t.is_reversed_pair ? 'italic' : 'normal' }}
                    onMouseEnter={e => { if (t.drill_path) e.currentTarget.style.background = '#eff6ff'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = i % 2 ? '#fafafa' : 'white'; }}
                  >
                    <td style={{ padding: '9px 12px', whiteSpace: 'nowrap' }}>{fmtDate(t.txn_date)}</td>
                    <td style={{ padding: '9px 12px' }}>
                      <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: t.txn_type === 'Receipt' ? '#dcfce7' : t.txn_type === 'Opening Balance' ? '#fef9c3' : (t.txn_type === 'Deposit Applied' || t.txn_type === 'Deposit Received') ? '#ede9fe' : t.txn_type === 'Deposit Refunded' ? '#fee2e2' : '#dbeafe', color: t.txn_type === 'Receipt' ? '#15803d' : t.txn_type === 'Opening Balance' ? '#854d0e' : (t.txn_type === 'Deposit Applied' || t.txn_type === 'Deposit Received') ? '#6d28d9' : t.txn_type === 'Deposit Refunded' ? '#b91c1c' : '#1d4ed8' }}>{t.txn_type}</span>
                    </td>
                    <td style={{ padding: '9px 12px', fontFamily: 'monospace', fontSize: 12, color: 'var(--text-muted)' }}>{t.ref_number || '—'}</td>
                    <td style={{ padding: '9px 12px', color: t.drill_path ? 'var(--primary)' : 'var(--text-muted)', fontWeight: t.drill_path ? 500 : 400, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {t.narration || '—'}{t.drill_path && <span style={{ fontSize: 10, marginLeft: 6, opacity: 0.6 }}>→</span>}
                    </td>
                    <td style={{ padding: '9px 12px', textAlign: 'right', color: t.debit > 0 ? '#1d4ed8' : 'var(--text-muted)' }}>{t.debit > 0 ? fmt(t.debit) : '—'}</td>
                    <td style={{ padding: '9px 12px', textAlign: 'right', color: t.credit > 0 ? '#16a34a' : 'var(--text-muted)' }}>{t.credit > 0 ? fmt(t.credit) : '—'}</td>
                    <td style={{ padding: '9px 12px', textAlign: 'right', fontWeight: 600, color: t.is_deposit_row ? 'var(--text-muted)' : (t.running_balance >= 0 ? '#dc2626' : '#16a34a') }}>
                      {t.is_deposit_row ? 'not AR' : <>{fmt(Math.abs(t.running_balance))} <span style={{ fontSize: 10 }}>{t.running_balance >= 0 ? 'Dr' : 'Cr'}</span></>}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '2px solid var(--border)', background: '#f8fafc', fontWeight: 700 }}>
                  <td colSpan={4} style={{ padding: '10px 12px' }}>Totals</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', color: '#1d4ed8' }}>{fmt(totalDebit)}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', color: '#16a34a' }}>{fmt(totalCredit)}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', color: balance >= 0 ? '#dc2626' : '#16a34a' }}>
                    {fmt(Math.abs(balance))} <span style={{ fontSize: 10 }}>{balance >= 0 ? 'Dr' : 'Cr'}</span>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
          </>
        )}
      </div>
    </div>
  );

  // ── SUMMARY VIEW (default) ──
  const thS = (col, align = 'right') => ({
    padding: '10px 12px', textAlign: align, fontWeight: 600, fontSize: 12, color: 'var(--text-muted)',
    cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap',
    background: sortKey === col ? '#f0f5ff' : '#f8fafc',
  });

  const filteredSummary = summaryRows.filter(r =>
    r.full_name?.toLowerCase().includes(clientSearch.toLowerCase()) ||
    r.client_code?.toLowerCase().includes(clientSearch.toLowerCase())
  );

  const agentGroups = agentGroupsMemo.filter(g =>
    g.prefix?.toLowerCase().includes(clientSearch.toLowerCase()) ||
    g.clients.some(c => c.full_name?.toLowerCase().includes(clientSearch.toLowerCase()) || c.client_code?.toLowerCase().includes(clientSearch.toLowerCase()))
  );

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20 }}>Accounts Receivable</h2>
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Client balances & aging — click any row to view full ledger</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <select className="form-input" style={{ fontSize: 13 }} value={branchFilter} onChange={e => { setBranchFilter(e.target.value); setSelectedClient(null); }}>
            <option value="">All Branches</option>
            {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
          <button className={`btn btn-sm ${groupView ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setGroupView(v => !v)}>
            {groupView ? '⊞ Group View' : '⊞ Group View'}
          </button>
          <button className="btn btn-secondary btn-sm" onClick={exportSummary}>Export CSV</button>
          <button className="btn btn-primary btn-sm" onClick={() => navigate('/vouchers/receipt/new')}>+ Receipt</button>
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <input className="form-input" placeholder="Search by client name, code or group..." value={clientSearch} onChange={e => setClientSearch(e.target.value)} style={{ maxWidth: 400 }} />
      </div>

      {/* Grand totals */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 12, marginBottom: 16 }}>
        <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 8, padding: '14px 16px' }}>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>Total Receivable</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#dc2626' }}>LKR {fmt(grandTotals.balance)}</div>
        </div>
        <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 8, padding: '14px 16px' }}>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>Clients with Balance</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#7c3aed' }}>{summaryRows.filter(r => r.balance > 0).length}</div>
        </div>
      </div>

      {/* Aging buckets */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8, marginBottom: 20 }}>
        {[
          { label: '0–30 Days (Current)', value: grandTotals.b0_30, color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' },
          { label: '31–60 Days', value: grandTotals.b31_60, color: '#d97706', bg: '#fffbeb', border: '#fde68a' },
          { label: '61–90 Days', value: grandTotals.b61_90, color: '#ea580c', bg: '#fff7ed', border: '#fed7aa' },
          { label: '90+ Days (Overdue)', value: grandTotals.b90plus, color: '#dc2626', bg: '#fef2f2', border: '#fecaca' },
        ].map(c => (
          <div key={c.label} style={{ background: c.bg, border: `1px solid ${c.border}`, borderRadius: 8, padding: '12px 14px' }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: c.color, marginBottom: 4 }}>{c.label}</div>
            <div style={{ fontSize: 17, fontWeight: 700, color: c.color }}>LKR {fmt(c.value)}</div>
          </div>
        ))}
      </div>

      {summaryError && <div style={{ color: '#dc2626', marginBottom: 12, fontSize: 13 }}>Error loading balances: {summaryError}</div>}

      <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
        {loadingSummary ? (
          <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}>Loading client balances...</div>
        ) : groupView ? (
          /* ── GROUP VIEW ── */
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border)', background: '#f8fafc' }}>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)' }}>Agent Group</th>
                  <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)' }}>Clients</th>
                  <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)' }}>Invoiced</th>
                  <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)' }}>Received</th>
                  <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)' }}>Balance Due</th>
                </tr>
              </thead>
              <tbody>
                {agentGroups.map((g) => {
                  const expanded = expandedGroups[g.prefix];
                  return (
                    <React.Fragment key={g.prefix}>
                      <tr
                        onClick={() => setExpandedGroups(prev => ({ ...prev, [g.prefix]: !prev[g.prefix] }))}
                        style={{ borderBottom: '1px solid var(--border)', background: expanded ? '#eff6ff' : 'white', cursor: 'pointer', fontWeight: 600 }}
                        onMouseEnter={e => e.currentTarget.style.background = '#eff6ff'}
                        onMouseLeave={e => e.currentTarget.style.background = expanded ? '#eff6ff' : 'white'}
                      >
                        <td style={{ padding: '10px 12px', fontFamily: 'monospace' }}>
                          <span style={{ marginRight: 8, color: 'var(--text-muted)' }}>{expanded ? '▼' : '▶'}</span>
                          {g.prefix}
                        </td>
                        <td style={{ padding: '10px 12px', textAlign: 'right', color: 'var(--text-muted)', fontWeight: 400 }}>{g.clients.length}</td>
                        <td style={{ padding: '10px 12px', textAlign: 'right', color: '#1d4ed8' }}>{fmt(g.debit)}</td>
                        <td style={{ padding: '10px 12px', textAlign: 'right', color: '#16a34a' }}>{fmt(g.credit)}</td>
                        <td style={{ padding: '10px 12px', textAlign: 'right', color: g.balance > 0 ? '#dc2626' : '#16a34a' }}>
                          {fmt(Math.abs(g.balance))} <span style={{ fontSize: 10 }}>{g.balance > 0 ? 'Dr' : 'Cr'}</span>
                        </td>
                      </tr>
                      {expanded && g.clients.map((r, i) => (
                        <tr key={r.id} onClick={() => setSelectedClient(r)}
                          style={{ borderBottom: '1px solid var(--border)', background: i % 2 ? '#f8fafc' : '#fafeff', cursor: 'pointer' }}
                          onMouseEnter={e => e.currentTarget.style.background = '#dbeafe'}
                          onMouseLeave={e => e.currentTarget.style.background = i % 2 ? '#f8fafc' : '#fafeff'}
                        >
                          <td style={{ padding: '8px 12px 8px 32px', fontFamily: 'monospace', fontSize: 12, color: 'var(--text-muted)' }}>{nicknameOnly(r.client_code) || r.client_code}</td>
                          <td style={{ padding: '8px 12px', textAlign: 'right', color: 'var(--text-muted)', fontSize: 12 }}>
                            <span style={{ fontFamily: 'sans-serif', fontSize: 12, color: '#374151' }}>{r.full_name}</span>
                          </td>
                          <td style={{ padding: '8px 12px', textAlign: 'right', color: '#1d4ed8', fontSize: 12 }}>{fmt(r.debit)}</td>
                          <td style={{ padding: '8px 12px', textAlign: 'right', color: '#16a34a', fontSize: 12 }}>{fmt(r.credit)}</td>
                          <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, fontSize: 12, color: r.balance > 0 ? '#dc2626' : '#16a34a' }}>
                            {fmt(Math.abs(r.balance))} <span style={{ fontSize: 10 }}>{r.balance > 0 ? 'Dr' : 'Cr'}</span>
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '2px solid var(--border)', background: '#f8fafc', fontWeight: 700 }}>
                  <td style={{ padding: '10px 12px' }}>Totals ({agentGroups.length} groups)</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', color: 'var(--text-muted)' }}>{summaryRows.length}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', color: '#1d4ed8' }}>{fmt(grandTotals.debit)}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', color: '#16a34a' }}>{fmt(grandTotals.credit)}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', color: '#dc2626' }}>{fmt(grandTotals.balance)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        ) : (
          /* ── FLAT VIEW ── */
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border)' }}>
                  <th style={thS('client_code', 'left')} onClick={() => handleSort('client_code')}>Code <SortIcon col="client_code" /></th>
                  <th style={thS('full_name', 'left')} onClick={() => handleSort('full_name')}>Client Name <SortIcon col="full_name" /></th>
                  <th style={thS('debit')} onClick={() => handleSort('debit')}>Invoiced <SortIcon col="debit" /></th>
                  <th style={thS('credit')} onClick={() => handleSort('credit')}>Received <SortIcon col="credit" /></th>
                  <th style={thS('balance')} onClick={() => handleSort('balance')}>Balance Due <SortIcon col="balance" /></th>
                  <th style={thS('depositBalance')} onClick={() => handleSort('depositBalance')}>Deposit Balance <SortIcon col="depositBalance" /></th>
                  <th style={thS('b0_30')} onClick={() => handleSort('b0_30')}>0–30d <SortIcon col="b0_30" /></th>
                  <th style={thS('b31_60')} onClick={() => handleSort('b31_60')}>31–60d <SortIcon col="b31_60" /></th>
                  <th style={thS('b61_90')} onClick={() => handleSort('b61_90')}>61–90d <SortIcon col="b61_90" /></th>
                  <th style={thS('b90plus')} onClick={() => handleSort('b90plus')}>90+d <SortIcon col="b90plus" /></th>
                </tr>
              </thead>
              <tbody>
                {filteredSummary.map((r, i) => (
                  <tr key={r.id} onClick={() => setSelectedClient(r)}
                    style={{ borderBottom: '1px solid var(--border)', background: i % 2 ? '#fafafa' : 'white', cursor: 'pointer' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#eff6ff'}
                    onMouseLeave={e => e.currentTarget.style.background = i % 2 ? '#fafafa' : 'white'}>
                    <td style={{ padding: '9px 12px', fontFamily: 'monospace', fontSize: 12, color: 'var(--text-muted)' }}>{nicknameOnly(r.client_code) || r.client_code}</td>
                    <td style={{ padding: '9px 12px' }}>
                      {r.agents?.tls_code && <div style={{ fontSize: 10, color: '#f07040', fontWeight: 600 }}>{r.agents.tls_code}</div>}
                      <div style={{ fontWeight: 500 }}>{r.full_name}</div>
                    </td>
                    <td style={{ padding: '9px 12px', textAlign: 'right', color: '#1d4ed8' }}>{fmt(r.debit)}</td>
                    <td style={{ padding: '9px 12px', textAlign: 'right', color: '#16a34a' }}>{fmt(r.credit)}</td>
                    <td style={{ padding: '9px 12px', textAlign: 'right', fontWeight: 700, color: r.balance > 0 ? '#dc2626' : '#16a34a' }}>
                      {fmt(Math.abs(r.balance))} <span style={{ fontSize: 10 }}>{r.balance > 0 ? 'Dr' : 'Cr'}</span>
                    </td>
                    <td style={{ padding: '9px 12px', textAlign: 'right', color: r.depositBalance > 0 ? '#7c3aed' : 'var(--text-muted)' }}>{r.depositBalance > 0 ? fmt(r.depositBalance) : '—'}</td>
                    <td style={{ padding: '9px 12px', textAlign: 'right', color: '#16a34a' }}>{r.b0_30 > 0 ? fmt(r.b0_30) : '—'}</td>
                    <td style={{ padding: '9px 12px', textAlign: 'right', color: '#d97706' }}>{r.b31_60 > 0 ? fmt(r.b31_60) : '—'}</td>
                    <td style={{ padding: '9px 12px', textAlign: 'right', color: '#ea580c' }}>{r.b61_90 > 0 ? fmt(r.b61_90) : '—'}</td>
                    <td style={{ padding: '9px 12px', textAlign: 'right', fontWeight: r.b90plus > 0 ? 600 : 400, color: r.b90plus > 0 ? '#dc2626' : 'var(--text-muted)' }}>{r.b90plus > 0 ? fmt(r.b90plus) : '—'}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '2px solid var(--border)', background: '#f8fafc', fontWeight: 700 }}>
                  <td colSpan={2} style={{ padding: '10px 12px' }}>Totals ({filteredSummary.length} clients)</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', color: '#1d4ed8' }}>{fmt(grandTotals.debit)}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', color: '#16a34a' }}>{fmt(grandTotals.credit)}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', color: '#dc2626' }}>{fmt(grandTotals.balance)}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', color: '#7c3aed' }}>{fmt(grandTotals.depositBalance)}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', color: '#16a34a' }}>{fmt(grandTotals.b0_30)}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', color: '#d97706' }}>{fmt(grandTotals.b31_60)}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', color: '#ea580c' }}>{fmt(grandTotals.b61_90)}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', color: '#dc2626' }}>{fmt(grandTotals.b90plus)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
